import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Field, FormSpy } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { sub } from 'date-fns';

import inputField from 'components/Form/inputField';
import DatePickerField, { dateTimePicker } from 'components/Form/DatePickerField';
import CoordsField from 'components/Form/CoordsField';
import useNetworkState from 'hooks/useNetworkState';
import listToOptions from 'utils/listToOptions';
import SelectBox from 'components/Form/SelectBox';
import { validate as validateForm } from 'components/Form/utils';
import { isRequired, isValidCoordinates } from 'components/Form/validationRules';
import { getCountries } from 'api/events';
import { SET_COUNTRY_CODE } from 'reducers/global';

let cachedCountries = [];

const NewEventStep2 = () => {
  const [countries, setCountries] = useState(cachedCountries);
  const dispatch = useDispatch();
  const { isLoading, setIsLoading, setDoneLoading, setFailedLoading } = useNetworkState();

  const countryOptions = useMemo(
    () => [
      {
        label: 'Selecciona un país',
        value: '',
      },
      ...listToOptions(countries),
    ],
    [countries],
  );

  const updateCountriesList = useCallback(
    _countries => {
      setCountries(_countries);
      cachedCountries = countries;
    },
    [countries],
  );

  useEffect(() => {
    setIsLoading();
    getCountries()
      .then(response => {
        updateCountriesList(response?.body?.data || []);
        setDoneLoading();
      })
      .catch(() => {
        setFailedLoading();
      });
  }, []); // eslint-disable-line

  const onCountryChange = ({ target }) => {
    const selectedCountryObject = countries?.find(({ id }) => +target.value === id);

    dispatch({
      type: SET_COUNTRY_CODE,
      payload: { countryCode: selectedCountryObject?.code || 'SV' },
    });
  };

  return (
    <Fragment>
      <h2 className="text-brandblue text-2xl font-bold mb-4">Paso 2</h2>

      <Field
        component={inputField(SelectBox)}
        name="country_id"
        inputProps={{
          label: 'País',
          options: countryOptions,
          disabled: isLoading,
        }}
        onChange={onCountryChange}
      />
      <div className="mb-6">
        <label className="font-roboto text-xl mb-2 block">Fecha de inicio</label>
        <Field
          name="start_date"
          component={inputField(DatePickerField)}
          inputProps={dateTimePicker({
            placeholder: 'Fecha de inicio',
          })}
        />
      </div>

      <div className="mb-6">
        <label className="font-roboto text-xl mb-2 block">Último dia de inscripción</label>
        <FormSpy
          subscription={{ values: true }}
          render={({ values }) => (
            <Field
              name="last_registration_date"
              component={inputField(DatePickerField)}
              inputProps={{
                maxDate: values?.start_date ? sub(values?.start_date, { days: 1 }) : undefined,
                placeholder: 'Último dia de inscripción',
                useMaxDate: true,
              }}
            />
          )}
        />
      </div>

      <div className="mb-6">
        <label className="font-roboto text-xl mb-2 block">
          Fecha límite para inscribir jugadores
        </label>
        <Field
          className="truncate"
          name="last_lineup_registration_at"
          component={inputField(DatePickerField)}
          inputProps={{
            placeholder: 'Fecha límite para inscribir jugadores',
          }}
        />
      </div>

      <div className="mb-6">
        <label className="font-roboto text-xl mb-2 block">Nombre del lugar</label>

        <Field
          name="place"
          component="input"
          placeholder="Nombre del lugar"
          className="border-brandblue border-solid border rounded-lg py-2 px-4 w-full text-lg"
        />
      </div>

      <div className="mb-2">
        <label className="font-roboto text-xl mb-2 block">Coordenadas</label>

        <Field name="coordinates" component={inputField(CoordsField)} />
      </div>
    </Fragment>
  );
};

export const validations = {
  country_id: [isRequired],
  start_date: [isRequired],
  last_registration_date: [isRequired],
  last_lineup_registration_at: [isRequired],
  place: [isRequired],
  coordinates: [isValidCoordinates],
};

export const validate = validateForm({ validations, raw: true, messages: true });

export default NewEventStep2;
